// src/pages/Communication.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationApi } from '../../feddback-services/api';
import './Communication.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

const Communication = () => {
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole') || 'candidate';
  const userName = localStorage.getItem('userName') || 'User';
  const userMobile = localStorage.getItem('userMobile') || '';
  const token = localStorage.getItem('token');
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedChat, setSelectedChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);

  const isAdmin = ['admin', 'super_admin'].includes(userRole);
  const isExternalAdmin = userRole === 'external_admin';
  const isCandidate = userRole === 'candidate';
  const canSendMessage = isAdmin || isExternalAdmin;

  const showToast = (type, message) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 4000);
  };

  // =============================================
  // EXTRACT SENDER FROM SUBJECT
  // =============================================
  const extractSenderFromSubject = (subject) => {
    if (!subject) return null;
    const match = subject.match(/Message from (.*?) to (.*)/);
    if (match) {
      return { sender: match[1].trim(), recipient: match[2].trim() };
    }
    return null;
  };

  // =============================================
  // GET SENDER NAME - All admin messages show "You"
  // =============================================
  const getSenderName = (msg) => {
    const isCurrentUserAdmin = isAdmin || isExternalAdmin;
    
    // If current user is Admin, all admin messages = "You"
    if (isCurrentUserAdmin) {
      // Check by sent_by
      if (msg.sent_by === 'admin' || 
          msg.sent_by === 'super_admin' ||
          msg.sent_by === userRole) {
        return 'You';
      }
      
      // Check by subject
      if (msg.subject) {
        const extracted = extractSenderFromSubject(msg.subject);
        if (extracted) {
          const sender = extracted.sender.toLowerCase();
          if (sender === 'admin' || sender === 'super_admin') {
            return 'You';
          }
        }
      }
    }
    
    // If current user is Candidate
    if (isCandidate) {
      if (msg.sent_by === userRole) {
        return 'You';
      }
    }
    
    // Check if sent_by is admin user ID from database
    if (msg.sent_by === '63c6c90d-1b2c-454b-8e6a-f31fbf4282d1') {
      return isAdmin ? 'You' : 'Admin';
    }
    
    // Extract from subject for other senders
    if (msg.subject) {
      const extracted = extractSenderFromSubject(msg.subject);
      if (extracted) {
        return extracted.sender;
      }
    }
    
    // From metadata
    if (msg.metadata?.sender_name) {
      return msg.metadata.sender_name;
    }
    
    // Incoming message from candidate
    if (!msg.sent_by || msg.sent_by === 'external' || msg.sent_by === 'null') {
      return msg.metadata?.recipient_name || msg.sender_name || selectedChat?.name || 'Candidate';
    }
    
    return msg.sender_name || 'Unknown';
  };

  // =============================================
  // CHECK IF MESSAGE IS OWN (For alignment)
  // =============================================
  const isOwnMessage = (msg) => {
    const isCurrentUserAdmin = isAdmin || isExternalAdmin;
    
    // If current user is Admin
    if (isCurrentUserAdmin) {
      // Check by sent_by
      if (msg.sent_by === 'admin' || 
          msg.sent_by === 'super_admin' ||
          msg.sent_by === userRole) {
        return true;
      }
      
      // Check by subject
      if (msg.subject) {
        const extracted = extractSenderFromSubject(msg.subject);
        if (extracted) {
          const sender = extracted.sender.toLowerCase();
          if (sender === 'admin' || sender === 'super_admin') {
            return true;
          }
        }
      }
    }
    
    // If current user is Candidate
    if (isCandidate) {
      if (msg.sent_by === userRole) {
        return true;
      }
    }
    
    return false;
  };

  // =============================================
  // FETCH COMMUNICATIONS FROM DATABASE
  // =============================================
  const fetchCommunicationsFromDB = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/communications?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      if (response.ok && result.success) {
        const dbMessages = result.data?.items || [];
        const savedMessages = localStorage.getItem('communication_messages');
        const localMessages = savedMessages ? JSON.parse(savedMessages) : [];
        
        const allMessages = [...dbMessages];
        localMessages.forEach(localMsg => {
          const exists = allMessages.some(dbMsg => 
            dbMsg.id === localMsg.id || dbMsg.temp_id === localMsg.id
          );
          if (!exists) allMessages.push(localMsg);
        });
        
        allMessages.sort((a, b) => {
          return new Date(a.created_at || a.timestamp || 0) - new Date(b.created_at || b.timestamp || 0);
        });
        
        setMessages(allMessages);
        buildChatList(applications, allMessages);
        return allMessages;
      }
    } catch (err) {
      console.error('Error fetching:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // =============================================
  // FETCH APPLICATIONS
  // =============================================
  const fetchApplications = async () => {
    try {
      const response = await applicationApi.list();
      const appData = response.data.data || response.data;
      const apps = appData.items || [];
      setApplications(apps);
      return apps;
    } catch (err) {
      console.error('Error:', err);
      return [];
    }
  };

  // =============================================
  // BUILD CHAT LIST
  // =============================================
  const buildChatList = (applicationsData, communicationsData) => {
    const chatMap = new Map();

    if (isAdmin || isExternalAdmin) {
      applicationsData.forEach(app => {
        const userId = app.candidate_id || app.id;
        if (!userId) return;
        
        const chatMessages = communicationsData.filter(msg => {
          return msg.application_id === app.id ||
                 msg.recipient_user_id === userId ||
                 msg.metadata?.recipient_mobile === app.candidate_mobile ||
                 msg.metadata?.recipient_name === app.candidate_name;
        });
        
        chatMessages.sort((a, b) => {
          return new Date(a.created_at || a.timestamp || 0) - new Date(b.created_at || b.timestamp || 0);
        });
        
        const lastMsg = chatMessages.length > 0 ? chatMessages[chatMessages.length - 1] : null;
        
        const unreadCount = chatMessages.filter(m => {
          return !m.read && !isOwnMessage(m);
        }).length;
        
        if (!chatMap.has(userId)) {
          chatMap.set(userId, {
            id: userId,
            name: app.candidate_name || 'Unknown Candidate',
            role: 'candidate',
            mobile: app.candidate_mobile,
            email: app.candidate_email,
            application_id: app.id,
            lastMessage: lastMsg ? lastMsg.message : `Applied for: ${app.requirement_title || 'Position'}`,
            lastMessageTime: lastMsg?.created_at || app.applied_at,
            unreadCount: unreadCount,
            messages: chatMessages,
            canReply: true,
            lastMsgSender: lastMsg ? getSenderName(lastMsg) : ''
          });
        }
      });

      if (isAdmin) {
        chatMap.set('external_admin', {
          id: 'external_admin',
          name: 'External Admin',
          role: 'external_admin',
          mobile: null,
          application_id: null,
          lastMessage: 'No messages',
          lastMessageTime: null,
          unreadCount: 0,
          messages: [],
          canReply: false,
          lastMsgSender: ''
        });
      }
    } else if (isCandidate) {
      const adminMessages = communicationsData.filter(msg => {
        return msg.sent_by === 'admin' || 
               msg.sent_by === 'super_admin' ||
               msg.metadata?.recipient_mobile === userMobile;
      });
      
      const unreadCount = adminMessages.filter(m => !m.read).length;
      const lastMsg = adminMessages.length > 0 ? adminMessages[adminMessages.length - 1] : null;
      
      chatMap.set('admin', {
        id: 'admin',
        name: 'Admin Team',
        role: 'admin',
        mobile: null,
        application_id: null,
        lastMessage: lastMsg ? lastMsg.message : 'Welcome!',
        lastMessageTime: lastMsg?.created_at || new Date().toISOString(),
        unreadCount: unreadCount,
        messages: adminMessages,
        canReply: true,
        lastMsgSender: lastMsg ? getSenderName(lastMsg) : ''
      });
    }

    const chatList = Array.from(chatMap.values());
    chatList.sort((a, b) => {
      if (!a.lastMessageTime) return 1;
      if (!b.lastMessageTime) return -1;
      return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
    });

    setChats(chatList);
    
    if (chatList.length > 0 && !selectedChat) {
      const chatWithUnread = chatList.find(c => c.unreadCount > 0);
      setSelectedChat(chatWithUnread || chatList[0]);
    }
  };

  // =============================================
  // MARK ALL AS READ
  // =============================================
  const markAllAsRead = () => {
    if (!selectedChat) return;
    
    const updatedMessages = messages.map(msg => {
      const isForThisChat = msg.recipient_user_id === selectedChat.id || 
                           msg.metadata?.recipient_mobile === selectedChat.mobile ||
                           msg.application_id === selectedChat.application_id;
      
      if (isForThisChat && !msg.read && !isOwnMessage(msg)) {
        return { ...msg, read: true, read_at: new Date().toISOString() };
      }
      return msg;
    });
    
    setMessages(updatedMessages);
    localStorage.setItem('communication_messages', JSON.stringify(updatedMessages));
    
    const updatedChats = chats.map(chat => {
      if (chat.id === selectedChat.id) {
        return { ...chat, unreadCount: 0 };
      }
      return chat;
    });
    setChats(updatedChats);
  };

  // =============================================
  // SEND MESSAGE
  // =============================================
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat || !canSendMessage || !selectedChat.mobile) {
      if (!selectedChat?.mobile) showToast('error', 'No mobile number');
      return;
    }

    setIsLoading(true);
    const tempId = `temp_${Date.now()}`;
    const messageText = newMessage.trim();
    setNewMessage('');

    const newMsg = {
      id: tempId,
      temp_id: tempId,
      message: messageText,
      sent_by: userRole,
      recipient_user_id: selectedChat.id,
      application_id: selectedChat.application_id,
      created_at: new Date().toISOString(),
      status: 'sending',
      read: true,
      channel: 'whatsapp',
      mobile: selectedChat.mobile,
      sender_name: userName,
      recipient_name: selectedChat.name,
      metadata: { recipient_name: selectedChat.name, recipient_mobile: selectedChat.mobile }
    };

    const updatedMessages = [...messages, newMsg];
    setMessages(updatedMessages);
    localStorage.setItem('communication_messages', JSON.stringify(updatedMessages));

    const updatedChats = chats.map(chat => {
      if (chat.id === selectedChat.id) {
        return { 
          ...chat, 
          messages: [...chat.messages, newMsg], 
          lastMessage: newMsg.message, 
          lastMessageTime: newMsg.created_at,
          lastMsgSender: 'You'
        };
      }
      return chat;
    });
    setChats(updatedChats);
    setSelectedChat(updatedChats.find(c => c.id === selectedChat.id));

    try {
      const payload = {
        message: messageText,
        channel: 'whatsapp',
        application_id: selectedChat.application_id || null,
        recipient_user_id: selectedChat.id || null,
        mobile_override: selectedChat.mobile || null,
        email_override: selectedChat.email || null,
        subject: `Message from ${userName} to ${selectedChat.name}`
      };

      const response = await fetch(`${API_BASE_URL}/api/communications/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const finalMessages = updatedMessages.map(msg => 
          msg.id === tempId ? { ...msg, status: 'sent', id: result.data?.id || msg.id } : msg
        );
        setMessages(finalMessages);
        localStorage.setItem('communication_messages', JSON.stringify(finalMessages));
        await fetchCommunicationsFromDB();
        showToast('success', `✅ Sent to ${selectedChat.name}`);
      } else {
        const finalMessages = updatedMessages.map(msg => 
          msg.id === tempId ? { ...msg, status: 'failed' } : msg
        );
        setMessages(finalMessages);
        localStorage.setItem('communication_messages', JSON.stringify(finalMessages));
        showToast('error', `❌ Failed: ${result.message || 'Unknown error'}`);
      }
    } catch (err) {
      const finalMessages = updatedMessages.map(msg => 
        msg.id === tempId ? { ...msg, status: 'failed' } : msg
      );
      setMessages(finalMessages);
      localStorage.setItem('communication_messages', JSON.stringify(finalMessages));
      showToast('error', '⚠️ Could not send');
    } finally {
      setIsLoading(false);
    }
  };

  // =============================================
  // HANDLE CHAT SELECT
  // =============================================
  const handleChatSelect = (chat) => {
    if (selectedChat?.id === chat.id) return;
    setSelectedChat(chat);
    
    if (chat.unreadCount > 0) {
      setTimeout(() => {
        markAllAsRead();
      }, 500);
    }
  };

  // =============================================
  // FORMAT TIME
  // =============================================
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return date.toLocaleDateString('en-US', { weekday: 'short' });
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // =============================================
  // GET ROLE BADGE
  // =============================================
  const getRoleBadge = (role) => {
    const roles = {
      admin: { class: 'role-admin', label: 'Admin' },
      super_admin: { class: 'role-admin', label: 'Super Admin' },
      external_admin: { class: 'role-external', label: 'Ext. Admin' },
      candidate: { class: 'role-candidate', label: 'Candidate' }
    };
    const r = roles[role] || roles.candidate;
    return <span className={`chat-role-badge ${r.class}`}>{r.label}</span>;
  };

  const getChannelIcon = (channel) => {
    if (channel === 'whatsapp') return <i className="fab fa-whatsapp" style={{ color: '#25D366' }}></i>;
    return null;
  };

  const getMessageStatus = (status) => {
    if (status === 'sending') {
      return <span className="msg-status sending"><i className="fas fa-spinner fa-spin"></i></span>;
    }
    if (status === 'sent') {
      return <span className="msg-status sent"><i className="fas fa-check-double" style={{ color: '#25D366' }}></i></span>;
    }
    if (status === 'failed') {
      return <span className="msg-status failed"><i className="fas fa-exclamation-circle" style={{ color: '#dc3545' }}></i></span>;
    }
    return null;
  };

  const getReadStatus = (msg) => {
    if (isOwnMessage(msg)) {
      if (msg.read) {
        return <span className="read-status seen"><i className="fas fa-check-double" style={{ color: '#34B7F1' }}></i> Seen</span>;
      }
      if (msg.status === 'sent') {
        return <span className="read-status delivered"><i className="fas fa-check-double" style={{ color: '#9e9e9e' }}></i> Delivered</span>;
      }
    }
    return null;
  };

  // =============================================
  // INITIAL LOAD
  // =============================================
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const apps = await fetchApplications();
        await fetchCommunicationsFromDB();
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setIsLoading(false);
      }
    };
    if (token) {
      loadData();
    } else {
      navigate('/login');
    }
  }, [token]);

  // =============================================
  // AUTO REFRESH
  // =============================================
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedChat) {
        fetchCommunicationsFromDB();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, selectedChat]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // =============================================
  // RENDER CHAT LIST
  // =============================================
  const renderChatList = () => {
    const listTitle = isCandidate ? 'Conversations' : `Candidates (${chats.filter(c => c.role === 'candidate').length})`;
    
    return (
      <div className="chat-list">
        <div className="chat-list-header">
          <i className="fas fa-users"></i>
          <span>{listTitle}</span>
        </div>
        <div className="chat-list-items">
          {loadingHistory ? (
            <div className="loading-chats"><i className="fas fa-spinner fa-spin"></i><p>Loading...</p></div>
          ) : chats.length === 0 ? (
            <div className="no-chats"><i className="fas fa-inbox"></i><p>No conversations</p></div>
          ) : (
            chats.filter(c => c.role !== 'external_admin' || isAdmin).map((chat) => {
              let lastMsgSender = chat.lastMsgSender || '';
              if (lastMsgSender === 'You') {
                lastMsgSender = 'You: ';
              } else if (lastMsgSender && lastMsgSender !== 'Unknown') {
                lastMsgSender = `${lastMsgSender}: `;
              }
              
              return (
                <button
                  key={chat.id}
                  className={`chat-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
                  onClick={() => handleChatSelect(chat)}
                >
                  <div className="chat-avatar">
                    {chat.name?.charAt(0) || 'U'}
                    {chat.mobile && <span className="status-dot online"></span>}
                  </div>
                  <div className="chat-info">
                    <div className="chat-name">
                      {chat.name}
                      {getRoleBadge(chat.role)}
                    </div>
                    <div className="chat-last-msg">
                      {lastMsgSender && (
                        <span className="last-msg-sender">{lastMsgSender}</span>
                      )}
                      <span className="last-msg-text">{chat.lastMessage || 'No messages'}</span>
                    </div>
                  </div>
                  <div className="chat-meta">
                    <span className="chat-time">{formatTime(chat.lastMessageTime)}</span>
                    {chat.unreadCount > 0 && (
                      <span className="chat-unread">{chat.unreadCount}</span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // =============================================
  // MAIN RENDER
  // =============================================
  return (
    <div className="communication-container">
      {toast.show && (
        <div className={`toast ${toast.type}`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast({ show: false, message: '', type: '' })}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="communication-card">
        <div className="comm-header">
          <h1><i className="fas fa-comments"></i> {isAdmin ? 'Admin Messages' : 'My Messages'}</h1>
          <div className="comm-header-right">
            <span className="comm-role">{isAdmin ? 'Admin' : 'Candidate'}</span>
          </div>
        </div>

        <div className="comm-body">
          {renderChatList()}

          <div className="chat-window">
            {selectedChat ? (
              <>
                <div className="chat-window-header">
                  <div className="chat-window-user">
                    <div className="chat-avatar">{selectedChat.name?.charAt(0) || 'U'}</div>
                    <div className="chat-window-info">
                      <span className="chat-window-name">{selectedChat.name}</span>
                      <span className="chat-window-role">
                        {getRoleBadge(selectedChat.role)}
                        {selectedChat.mobile && (
                          <span className="chat-mobile"><i className="fas fa-phone"></i> {selectedChat.mobile}</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="chat-status">
                    <span className="status-dot online"></span>
                    {selectedChat.mobile ? 'WhatsApp' : 'No WhatsApp'}
                  </div>
                </div>

                <div className="chat-messages">
                  {loadingHistory ? (
                    <div className="loading-messages"><i className="fas fa-spinner fa-spin"></i><p>Loading...</p></div>
                  ) : selectedChat.messages && selectedChat.messages.length > 0 ? (
                    selectedChat.messages.map((msg, idx) => {
                      const senderName = getSenderName(msg);
                      const isOwn = isOwnMessage(msg);
                      
                      return (
                        <div key={msg.id || idx} className={`message ${isOwn ? 'own' : 'other'}`}>
                          <div className="message-content">
                            <div className="message-sender">
                              <span className="sender-name">
                                {senderName}
                                {!isOwn && msg.channel && getChannelIcon(msg.channel)}
                              </span>
                              {!isOwn && getRoleBadge(selectedChat?.role)}
                            </div>
                            <div className="message-text">{msg.message}</div>
                            <div className="message-time">
                              {formatTime(msg.created_at)}
                              {getMessageStatus(msg.status)}
                              {getReadStatus(msg)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-messages">
                      <i className="fas fa-comment-dots"></i>
                      <p>No messages</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {canSendMessage && selectedChat.mobile ? (
                  <form onSubmit={handleSendMessage} className="chat-input">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={`Send WhatsApp to ${selectedChat.name}...`}
                      className="message-input"
                      disabled={isLoading}
                    />
                    <button type="submit" className="send-btn" disabled={!newMessage.trim() || isLoading}>
                      <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                    </button>
                  </form>
                ) : (
                  <div className="chat-input read-only">
                    <p><i className="fas fa-eye"></i> {!selectedChat.mobile ? 'No mobile number' : 'Read only'}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="no-chat-selected">
                <i className="fas fa-users"></i>
                <h3>Select a conversation</h3>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Communication;